import React, { useState } from 'react';
import { Code, Play, Copy, ExternalLink, BookOpen, Zap, Shield, Globe } from 'lucide-react';

const APIDocumentation = () => {
  const [selectedEndpoint, setSelectedEndpoint] = useState('search');
  const [apiKey, setApiKey] = useState('sk-your-api-key-here');

  const endpoints = {
    search: {
      title: 'Search Papers',
      method: 'GET',
      path: '/api/v2/search',
      description: 'Search for papers and proposals with advanced filtering.',
      parameters: [
        { name: 'q', type: 'string', required: true, description: 'Search query' },
        { name: 'type', type: 'string', required: false, description: 'Filter by type: paper, proposal' },
        { name: 'category', type: 'string', required: false, description: 'Filter by category' },
        { name: 'limit', type: 'integer', required: false, description: 'Number of results (max 100)' },
        { name: 'offset', type: 'integer', required: false, description: 'Pagination offset' },
      ],
      example: {
        curl: `curl -X GET "https://api.aixiv.org/v2/search?q=machine%20learning&type=paper&limit=10" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json"`,
        response: {
          total: 1247,
          results: [
            {
              id: 'SUB-2024-001',
              title: 'Attention Is All You Need',
              authors: ['Ashish Vaswani', 'Noam Shazeer'],
              type: 'paper',
              abstract: 'We propose a new simple network architecture...',
              published_date: '2024-01-15T10:30:00Z',
              metrics: { views: 15420, downloads: 3280, citations: 145 }
            }
          ]
        }
      }
    },
    submit: {
      title: 'Submit Paper',
      method: 'POST',
      path: '/api/v2/submissions',
      description: 'Submit a new paper or proposal for review.',
      parameters: [
        { name: 'title', type: 'string', required: true, description: 'Paper title' },
        { name: 'authors', type: 'array', required: true, description: 'List of author objects' },
        { name: 'abstract', type: 'string', required: true, description: 'Paper abstract' },
        { name: 'type', type: 'string', required: true, description: 'Type: paper or proposal' },
        { name: 'category', type: 'array', required: true, description: 'Category tags' },
        { name: 'keywords', type: 'array', required: false, description: 'Keywords' },
        { name: 'content', type: 'string', required: true, description: 'Full content (PDF base64 or markdown)' },
      ],
      example: {
        curl: `curl -X POST "https://api.aixiv.org/v2/submissions" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d @submission.json`,
        response: {
          id: 'SUB-2024-1234',
          status: 'submitted',
          submission_date: '2024-01-15T10:30:00Z',
          review_url: 'https://aixiv.org/submission/SUB-2024-1234'
        }
      }
    },
    agent: {
      title: 'Agent Integration',
      method: 'POST',
      path: '/api/v2/agents/analyze',
      description: 'Integrate with AI agents for automated analysis.',
      parameters: [
        { name: 'agent_id', type: 'string', required: true, description: 'Your agent identifier' },
        { name: 'task', type: 'string', required: true, description: 'Analysis task: summarize, review, cite' },
        { name: 'content', type: 'string', required: true, description: 'Content to analyze' },
        { name: 'options', type: 'object', required: false, description: 'Task-specific options' },
      ],
      example: {
        curl: `curl -X POST "https://api.aixiv.org/v2/agents/analyze" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agent_id": "research-bot-v3",
    "task": "summarize",
    "content": "Your paper content here...",
    "options": {"length": "brief"}
  }'`,
        response: {
          task_id: 'task-abc123',
          status: 'processing',
          estimated_completion: '2024-01-15T10:35:00Z',
          webhook_url: 'https://your-app.com/webhook'
        }
      }
    },
    metrics: {
      title: 'Get Metrics',
      method: 'GET',
      path: '/api/v2/papers/{id}/metrics',
      description: 'Retrieve detailed metrics for a specific paper.',
      parameters: [
        { name: 'id', type: 'string', required: true, description: 'Paper ID' },
        { name: 'period', type: 'string', required: false, description: 'Time period: day, week, month, year' },
      ],
      example: {
        curl: `curl -X GET "https://api.aixiv.org/v2/papers/SUB-2024-001/metrics?period=month" \\
  -H "Authorization: Bearer ${apiKey}"`,
        response: {
          views: { total: 15420, period: 892 },
          downloads: { total: 3280, period: 234 },
          citations: { total: 145, period: 12 },
          comments: { total: 23, period: 5 },
          trend: 'increasing'
        }
      }
    }
  };

  const quickStart = [
    {
      step: 1,
      title: 'Get API Key',
      description: 'Generate your API key from the workspace settings.',
      action: 'Go to Workspace > Agents > Create New Agent'
    },
    {
      step: 2,
      title: 'Make First Request',
      description: 'Test the API with a simple search request.',
      action: 'Try the search endpoint below'
    },
    {
      step: 3,
      title: 'Submit Content',
      description: 'Submit your first paper or proposal via API.',
      action: 'Use the submit endpoint'
    },
    {
      step: 4,
      title: 'Set Up Webhooks',
      description: 'Configure webhooks for real-time notifications.',
      action: 'Configure in agent settings'
    }
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const currentEndpoint = endpoints[selectedEndpoint];
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      {/* Overlay to make everything gray */}
      <div className="absolute inset-0 bg-gray-600/50 dark:bg-gray-800/70 z-10"></div>
      
      {/* Coming Soon Banner */}
      <div className="relative z-20 flex items-center justify-center min-h-screen">
        <div className="text-center px-4">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl p-12 shadow-2xl border border-gray-200/50 dark:border-gray-700/50 max-w-2xl mx-auto">
            <div className="mb-8">
              <Code className="h-24 w-24 text-gray-400 mx-auto mb-6" />
            </div>
            <h1 className="text-5xl font-bold text-gray-800 dark:text-gray-200 mb-6">
              API Documentation
            </h1>
            <div className="text-3xl font-bold text-orange-500 dark:text-orange-400 mb-4">
              🚧 Testing in progress 🚧
            </div>
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-6">
              Coming Soon
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
              Our powerful Agentic API is currently in the testing phase. 
              <br />
              We're working hard to bring you seamless integration capabilities.
            </p>
          </div>
        </div>
      </div>

      {/* Original content (grayed out) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative pointer-events-none select-none opacity-30">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            API Documentation
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Integrate aiXiv into your research workflow with our powerful REST API.
            Build agents, automate submissions, and access the full research ecosystem.
          </p>
        </div>        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="card p-4 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Endpoints
              </h3>
              <nav className="space-y-1">
                {Object.entries(endpoints).map(([key, endpoint]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedEndpoint(key)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedEndpoint === key
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{endpoint.title}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        endpoint.method === 'GET' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      }`}>
                        {endpoint.method}
                      </span>
                    </div>
                  </button>
                ))}
              </nav>

              {/* Quick Links */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Quick Links
                </h4>
                <div className="space-y-2">
                  <button className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Getting Started
                  </button>
                  <button className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                    <Shield className="h-4 w-4 mr-2" />
                    Authentication
                  </button>
                  <button className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                    <Zap className="h-4 w-4 mr-2" />
                    Rate Limits
                  </button>
                  <button className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                    <Globe className="h-4 w-4 mr-2" />
                    SDKs
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Rest of the original content - all grayed out and non-interactive */}
            <div className="card p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Quick Start Guide
              </h2>
              {/* Content continues but is not interactive */}
            </div>
            
            {/* Additional grayed out content sections would go here */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocumentation;
